import React, { useRef, useEffect, useState } from "react";
import SlidePanel from "./SlidePanel";
import { toast } from "react-toastify";
import { X, RotateCcw } from "lucide-react";

const IdentityScanningPanel = ({
  id_R,
  closeIdentityPanel,
  isIdentityPanelOpen,
  identificationType,
  setIdentificationType,
  country,
  setCountry,
  idFrontImage,
  setIdFrontImage,
  idBackImage,
  setIdBackImage,
  passportImage,
  setPassportImage,
  handleGetFilesNames,
  isContinueEnab,
  setIsContinueEnab,
  photoFile,
  setPhotoFile,
  uploadKenyaIdBack,
  uploadKenyaIdFront,
  uploadPassport,
}) => {
  const fileInputFront = useRef(null);
  const fileInputBack = useRef(null);
  const fileInputPassport = useRef(null);
  const fileInputPhoto = useRef(null);

  const [isBackUploading, setIsBackUploading] = useState(false);
  const [backStatus, setBackStatus] = useState(null);

  useEffect(() => {
    if (identificationType === "National Id") {
      setIsContinueEnab(
        idFrontImage !== null && idBackImage !== null && photoFile !== null,
      );
    } else if (identificationType === "passport") {
      setIsContinueEnab(passportImage !== null && photoFile !== null);
    } else {
      setIsContinueEnab(false);
    }
  }, [identificationType, idFrontImage, idBackImage, passportImage, photoFile]);

  const openFrontFilePicker = () => {
    fileInputFront?.current?.click();
  };

  const validateFrontId = (res) => {
    const data = res?.data;

    return Boolean(
      data?.idNumber && data?.fullNames && data?.dateOfBirth && data?.sex,
    );
  };

  const [isFrontUploading, setIsFrontUploading] = useState(false);

  const handleFrontFileChange = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setIsFrontUploading(true);

    try {
      const res = await uploadKenyaIdFront(file);

      if (!validateFrontId(res)) {
        toast?.error("Invalid Front ID. Upload the front side of the ID.", {
          autoClose: false,
        });

        return;
      }

      setIdFrontImage(file);
    } catch (err) {
      toast?.error("Please upload a clear front side of a valid National ID.", {
        autoClose: false,
      });
    } finally {
      setIsFrontUploading(false);
    }
  };

  const openBackFilePicker = () => {
    fileInputBack.current.click();
  };

  const validateBackId = (res) => {
    const data = res?.data;

    return data?.foundAny === true;
  };

  const validatePassport = (res) => {
    const data = res?.data;

    return Boolean(data?.passportNumber && data?.fullName && data?.dateOfBirth);
  };

  const handleBackFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsBackUploading(true);
    setBackStatus(null);

    try {
      const res = await uploadKenyaIdBack(file);

      if (!validateBackId(res)) {
        setBackStatus("error");
        toast?.error("Invalid Back ID. Upload the back side of the ID.", {
          autoClose: false,
        });
        return;
      }

      setIdBackImage(file);
      setBackStatus("success");
    } catch (err) {
      setBackStatus("error");
      toast?.error("Please upload a clear back side of a valid National ID.", {
        autoClose: false,
      });
    } finally {
      setIsBackUploading(false);
    }
  };

  const openPassportFilePicker = () => {
    fileInputPassport.current.click();
  };

  const [isPassportUploading, setIsPassportUploading] = useState(false);

  const handlePassportFileChange = async (e) => {
    const file = e?.target?.files[0];

    if (!file) return;

    setIsPassportUploading(true);

    try {
      const res = await uploadPassport(file);

      if (!validatePassport(res)) {
        toast?.error("Invalid Passport. Upload a clear valid passport image.", {
          autoClose: false,
        });

        return;
      }

      setPassportImage(file);
    } catch (err) {
      toast?.error("Please upload a clear valid passport image.", {
        autoClose: false,
      });
    } finally {
      setIsPassportUploading(false);
    }
  };
  const openPhotoPicker = () => {
    fileInputPhoto.current.click();
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    setPhotoFile(file);
  };

  const truncateFileName = (fileName) => {
    const maxLength = 12;
    const extensionIndex = fileName.lastIndexOf(".");
    const nameWithoutExtension = fileName.substring(0, extensionIndex);
    const truncatedName =
      nameWithoutExtension.length > maxLength
        ? `${nameWithoutExtension.substring(0, maxLength)}...`
        : nameWithoutExtension;
    return `${truncatedName}.${fileName.substring(extensionIndex + 1)}`;
  };

  const handleReplaceFile = (type) => {
    switch (type) {
      case "front":
        if (fileInputFront.current) fileInputFront.current.click();
        break;

      case "back":
        if (fileInputBack.current) fileInputBack.current.click();
        break;

      case "passport":
        if (fileInputPassport.current) fileInputPassport.current.click();
        break;

      case "photo":
        if (fileInputPhoto.current) fileInputPhoto.current.click();
        break;

      default:
        break;
    }
  };

  return (
    <>
      <SlidePanel isOpen={isIdentityPanelOpen} onClose={closeIdentityPanel}>
        <div className="relative flex flex-col h-full w-full bg-white rounded-[12px]">
          <div
            onClick={closeIdentityPanel}
            className="absolute top-[0px] cursor-pointer h-[40px] w-[40px] bg-[#FF0000] rounded-tr-[10px] rounded-bl-[20px] right-[0px] flex items-center justify-center"
          >
            <X color="#ffffff" />
          </div>
          <div className="flex w-full h-full">
            <div className="w-full flex flex-col">
              <div className="mt-5 items-center px-5 ">
                <p className="text-[#000] text-xl font-bold">
                  New Member - Personal Details
                </p>
                <div className="text-[#000] text-[15px] font-[300]">
                  <p className="w-full">
                    Upload member’s National ID to autofill Personal Details
                  </p>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto px-5 mt-3">
                <div className="pb-6">
                  <div className="flex-col flex w-full justify-between mt-5 mr-4">
                    <div className="flex flex-col justify-center">
                      <label
                        htmlFor="country"
                        className="mb-1 text-md font-normal text-[#000]"
                      >
                        Country of citizenship
                      </label>
                      <select
                        name="county"
                        id="county"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="w-full h-10 px-4 rounded-[4px] bg-[#EAEAEA] border-[1px] border-[#EAEAEA]"
                      >
                        <option value="">Select county</option>
                        <option value="Kenya">Kenya</option>
                        <option value="United States">United States</option>
                      </select>
                    </div>
                    <div className="flex flex-col justify-center">
                      <label
                        htmlFor="county"
                        className="mb-1 text-md font-normal text-[#000]"
                      >
                        Select Identity document type
                      </label>
                      <select
                        name="Idtype"
                        id="Idtype"
                        value={identificationType}
                        onChange={(e) => setIdentificationType(e.target.value)}
                        className="w-[100%] h-10 px-4 border-[1px] rounded-[4px] bg-[#EAEAEA] border-[1px] border-[#EAEAEA]"
                      >
                        <option value="">Select county</option>
                        <option value="National Id">National Id</option>
                        <option value="passport">Passport</option>
                      </select>
                    </div>
                  </div>
                  {identificationType === "National Id" && (
                    <div>
                      <div className="flex items-center mt-8">
                        <div className="w-full">
                          <p className="text-left text-[16px] mb-2 text-[#000]">
                            Upload document (Front page of ID)
                          </p>
                          <div
                            onClick={
                              !isFrontUploading
                                ? openFrontFilePicker
                                : undefined
                            }
                            className={`w-full h-[200px] rounded-[5px] bg-[#EAEAEA] border-[1px] border-[#000000] flex items-center justify-center flex-col cursor-pointer ${
                              isFrontUploading
                                ? "opacity-60 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            <svg
                              width="45"
                              height="45"
                              viewBox="0 0 45 45"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M21.5625 29.2069V12.9881L17.1938 17.3569L15.8663 16.0106L22.5 9.375L29.1356 16.0106L27.8081 17.3588L23.4375 12.9881V29.2069H21.5625ZM12.405 35.625C11.5413 35.625 10.8206 35.3363 10.2431 34.7588C9.66562 34.1812 9.37625 33.46 9.375 32.595V28.0519H11.25V32.595C11.25 32.8838 11.37 33.1488 11.61 33.39C11.85 33.6313 12.1144 33.7512 12.4031 33.75H32.5969C32.8844 33.75 33.1488 33.63 33.39 33.39C33.6313 33.15 33.7512 32.885 33.75 32.595V28.0519H35.625V32.595C35.625 33.4588 35.3363 34.1794 34.7588 34.7569C34.1812 35.3344 33.46 35.6238 32.595 35.625H12.405Z"
                                fill="black"
                              />
                            </svg>
                            <input
                              type="file"
                              ref={fileInputFront}
                              onChange={handleFrontFileChange}
                              style={{ display: "none" }}
                            />

                            <p className="text-#005C53B8 text-16 text-center">
                              {!idFrontImage ? (
                                <>
                                  Drag and Drop or{" "}
                                  <span style={{ color: "#06B217" }}>
                                    Choose file
                                  </span>{" "}
                                  here
                                </>
                              ) : (
                                truncateFileName(idFrontImage?.name)
                              )}
                            </p>
                            {isFrontUploading && (
                              <p className="text-sm text-[#074073] mt-2">
                                Validating Front ID...
                              </p>
                            )}

                            {idFrontImage && (
                              <div className="flex flex-col items-center mt-2">
                                <p className="text-#005C53B8 w-90% truncate text-16 text-center">
                                  {idFrontImage?.type} (
                                  {(idFrontImage?.size / 1024)?.toFixed(2)} KB)
                                </p>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReplaceFile("front");
                                  }}
                                  className="mt-3 flex items-center gap-2 px-4 py-2 rounded-[5px] bg-[#E6F0FF] hover:bg-[#D9E9FF] text-[#000000] transition-all"
                                >
                                  <RotateCcw size={16} />
                                  Replace File
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center mt-8">
                        <div className="w-full">
                          <p className="text-left text-[16px] mb-2 text-[#000]">
                            Upload document (back page of ID)
                          </p>
                          <div
                            onClick={
                              !isBackUploading ? openBackFilePicker : undefined
                            }
                            className={`w-full h-[200px] rounded-[5px] bg-[#EAEAEA] border-[1px] border-[#000000] flex items-center justify-center flex-col cursor-pointer ${backStatus === "success" ? "bg-[#EAEAEA] border-[1px] border-[#000000]" : ""} ${backStatus === "error" ? "bg-[#EAEAEA] border-[1px] border-[#000000]" : ""} ${isBackUploading ? "opacity-60 cursor-not-allowed" : ""} `}
                          >
                            <svg
                              width="45"
                              height="45"
                              viewBox="0 0 45 45"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M21.5625 29.2069V12.9881L17.1938 17.3569L15.8663 16.0106L22.5 9.375L29.1356 16.0106L27.8081 17.3588L23.4375 12.9881V29.2069H21.5625ZM12.405 35.625C11.5413 35.625 10.8206 35.3363 10.2431 34.7588C9.66562 34.1812 9.37625 33.46 9.375 32.595V28.0519H11.25V32.595C11.25 32.8838 11.37 33.1488 11.61 33.39C11.85 33.6313 12.1144 33.7512 12.4031 33.75H32.5969C32.8844 33.75 33.1488 33.63 33.39 33.39C33.6313 33.15 33.7512 32.885 33.75 32.595V28.0519H35.625V32.595C35.625 33.4588 35.3363 34.1794 34.7588 34.7569C34.1812 35.3344 33.46 35.6238 32.595 35.625H12.405Z"
                                fill="black"
                              />
                            </svg>

                            <input
                              type="file"
                              ref={fileInputBack}
                              onChange={handleBackFileChange}
                              style={{ display: "none" }}
                            />

                            <p className="text-#005C53B8 text-16 text-center">
                              {!idBackImage ? (
                                <>
                                  Drag and Drop or{" "}
                                  <span style={{ color: "#06B217" }}>
                                    Choose file
                                  </span>{" "}
                                  here
                                </>
                              ) : (
                                truncateFileName(idBackImage?.name)
                              )}
                            </p>

                            {isBackUploading && (
                              <p className="text-sm text-[#074073] mt-2">
                                Validating Back ID...
                              </p>
                            )}

                            {idBackImage && (
                              <div className="flex flex-col items-center mt-2">
                                <p className="text-#005C53B8 text-16">
                                  {idBackImage?.type} (
                                  {(idBackImage?.size / 1024)?.toFixed(2)} KB)
                                </p>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleReplaceFile("back");
                                  }}
                                  className="mt-3 flex items-center gap-2 px-4 py-2 rounded-[5px] bg-[#E6F0FF] hover:bg-[#D9E9FF] text-[#000000] transition-all"
                                >
                                  <RotateCcw size={16} />
                                  <span className="text-sm font-medium">
                                    Replace File
                                  </span>
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {identificationType === "passport" && (
                    <div className="flex items-center mt-[40px]">
                      <div className="w-full">
                        <p className="text-left text-[16px] mb-2 text-[#000]">
                          Upload document (Should capture bio data page & first
                          page)
                        </p>
                        <div
                          onClick={
                            !isPassportUploading
                              ? openPassportFilePicker
                              : undefined
                          }
                          className={`w-[100%] h-[200px] rounded-[5px] bg-[#EAEAEA] border-[1px] border-[#000000] flex items-center justify-center flex-col cursor-pointer ${
                            isPassportUploading
                              ? "opacity-60 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          <svg
                            width="45"
                            height="45"
                            viewBox="0 0 45 45"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M21.5625 29.2069V12.9881L17.1938 17.3569L15.8663 16.0106L22.5 9.375L29.1356 16.0106L27.8081 17.3588L23.4375 12.9881V29.2069H21.5625ZM12.405 35.625C11.5413 35.625 10.8206 35.3363 10.2431 34.7588C9.66562 34.1812 9.37625 33.46 9.375 32.595V28.0519H11.25V32.595C11.25 32.8838 11.37 33.1488 11.61 33.39C11.85 33.6313 12.1144 33.7512 12.4031 33.75H32.5969C32.8844 33.75 33.1488 33.63 33.39 33.39C33.6313 33.15 33.7512 32.885 33.75 32.595V28.0519H35.625V32.595C35.625 33.4588 35.3363 34.1794 34.7588 34.7569C34.1812 35.3344 33.46 35.6238 32.595 35.625H12.405Z"
                              fill="black"
                            />
                          </svg>
                          <input
                            type="file"
                            ref={fileInputPassport}
                            onChange={handlePassportFileChange}
                            style={{ display: "none" }}
                          />

                          <p className="text-#005C53B8 text-16 text-center">
                            {!passportImage ? (
                              <>
                                Drag and Drop or{" "}
                                <span style={{ color: "#06B217" }}>
                                  Choose file
                                </span>{" "}
                                here
                              </>
                            ) : (
                              truncateFileName(passportImage?.name)
                            )}
                          </p>

                          {isPassportUploading && (
                            <p className="text-sm text-[#074073] mt-2">
                              Validating Passport...
                            </p>
                          )}
                          {passportImage && (
                            <div className="flex flex-col items-center mt-2">
                              <p className="text-#005C53B8 w-90% truncate text-16 text-center">
                                {passportImage?.type} (
                                {(passportImage?.size / 1024).toFixed(2)} KB)
                              </p>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleReplaceFile("passport");
                                }}
                                className="mt-3 flex items-center gap-2 px-4 py-2 rounded-[5px] bg-[#E6F0FF] hover:bg-[#D9E9FF] text-[#000000] transition-all"
                              >
                                <RotateCcw size={16} />
                                <span className="text-sm font-medium">
                                  Replace File
                                </span>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Passport Size Photo Upload */}
                  <div className="flex items-center mt-8">
                    <div className="w-full">
                      <p className="text-left text-[16px] mb-2 text-[#000]">
                        Upload passport size photo
                      </p>
                      <div
                        onClick={openPhotoPicker}
                        className="w-[100%] h-[200px] rounded-[5px] bg-[#EAEAEA] border-[1px] border-[#000000] flex items-center justify-center flex-col cursor-pointer"
                      >
                        <svg
                          width="45"
                          height="45"
                          viewBox="0 0 45 45"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21.5625 29.2069V12.9881L17.1938 17.3569L15.8663 16.0106L22.5 9.375L29.1356 16.0106L27.8081 17.3588L23.4375 12.9881V29.2069H21.5625ZM12.405 35.625C11.5413 35.625 10.8206 35.3363 10.2431 34.7588C9.66562 34.1812 9.37625 33.46 9.375 32.595V28.0519H11.25V32.595C11.25 32.8838 11.37 33.1488 11.61 33.39C11.85 33.6313 12.1144 33.7512 12.4031 33.75H32.5969C32.8844 33.75 33.1488 33.63 33.39 33.39C33.6313 33.15 33.7512 32.885 33.75 32.595V28.0519H35.625V32.595C35.625 33.4588 35.3363 34.1794 34.7588 34.7569C34.1812 35.3344 33.46 35.6238 32.595 35.625H12.405Z"
                            fill="black"
                          />
                        </svg>
                        <input
                          type="file"
                          ref={fileInputPhoto}
                          onChange={handlePhotoChange}
                          style={{ display: "none" }}
                        />

                        <p className="text-#005C53B8 text-16 text-center">
                          {!photoFile ? (
                            <>
                              Drag and Drop or{" "}
                              <span style={{ color: "#06B217" }}>
                                Choose file
                              </span>{" "}
                              here
                            </>
                          ) : (
                            truncateFileName(photoFile?.name)
                          )}
                        </p>
                        {/* <p className="text-#005C53B8 text-16 text-center">
                      {!photoFile
                        ? "Drag your file(s) here or browse"
                        : photoFile.name}
                    </p> */}

                        {photoFile && (
                          <div className="flex flex-col items-center mt-2">
                            <p className="text-#005C53B8 w-90% truncate text-16 text-center">
                              {photoFile.type} (
                              {(photoFile.size / 1024).toFixed(2)} KB)
                            </p>

                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReplaceFile("photo");
                              }}
                              className="mt-3 flex items-center gap-2 px-4 py-2 rounded-[5px] bg-[#E6F0FF] hover:bg-[#D9E9FF] text-[#000000] transition-all"
                            >
                              <RotateCcw size={16} />
                              <span className="text-sm font-medium">
                                Replace File
                              </span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <button
                  onClick={handleGetFilesNames}
                  data-modal-toggle={id_R}
                  type="button"
                  className={`text-white w-full hover:bg-[#005C53] focus:outline-none font-medium rounded-[5px] text-sm px-8 py-2.5 text-center ${
                    isContinueEnab
                      ? "bg-[#005C53]"
                      : "bg-[#D9D9D9] cursor-not-allowed"
                  }`}
                  disabled={!isContinueEnab}
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
        </div>
      </SlidePanel>
    </>
  );
};

export default IdentityScanningPanel;
